/**
 * Closest-slot expand matrix for creative 16x9 / 2x3 masters (AI-1417).
 * Copied from gaia-brooklyn-api/lib/services/images/closest-slot-matrix.ts
 * @module gui/components/images-upload-bulk-page/closest-slot-matrix
 */
import { CONTENT_TYPE_CLIP, CONTENT_TYPE_COURSE } from '../../../services/content/content-types'
import {
  ASSET_CONTENT_REFERENCE_TYPE_1X1_NO_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_1X1_WITH_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_D6_KEYART_NO_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_D6_KEYART_WITH_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_KEYART_2X3_WITH_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_KEYART_4K_NO_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_KEYART_4K_WITH_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_KEYART_4X3_WITH_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_POSTER_3X4,
  ASSET_CONTENT_REFERENCE_TYPE_POSTER_IMAGE,
} from '../../../services/assets/content-reference-types'

const NO_TEXT_16X9_MASTERS = Object.freeze([
  ASSET_CONTENT_REFERENCE_TYPE_D6_KEYART_NO_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_KEYART_4K_NO_TEXT,
])

const WITH_TEXT_16X9_MASTERS = Object.freeze([
  ASSET_CONTENT_REFERENCE_TYPE_D6_KEYART_WITH_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_KEYART_4K_WITH_TEXT,
])

const TWO_BY_THREE_MASTERS = Object.freeze([ASSET_CONTENT_REFERENCE_TYPE_KEYART_2X3_WITH_TEXT])

const NO_TEXT_16X9_TARGETS = Object.freeze([
  { type: ASSET_CONTENT_REFERENCE_TYPE_D6_KEYART_NO_TEXT, method: 'copy', genFill: false },
  {
    type: ASSET_CONTENT_REFERENCE_TYPE_KEYART_4K_NO_TEXT,
    method: 'upscale',
    width: 3840,
    height: 2160,
    genFill: false,
  },
  {
    type: ASSET_CONTENT_REFERENCE_TYPE_1X1_NO_TEXT,
    method: 'gen-fill',
    width: 3000,
    height: 3000,
    genFill: true,
  },
])

const WITH_TEXT_16X9_TARGETS = Object.freeze([
  { type: ASSET_CONTENT_REFERENCE_TYPE_D6_KEYART_WITH_TEXT, method: 'copy', genFill: false },
  {
    type: ASSET_CONTENT_REFERENCE_TYPE_KEYART_4K_WITH_TEXT,
    method: 'upscale',
    width: 3840,
    height: 2160,
    genFill: false,
  },
  {
    type: ASSET_CONTENT_REFERENCE_TYPE_KEYART_4X3_WITH_TEXT,
    method: 'gen-fill',
    width: 2048,
    height: 1536,
    genFill: true,
  },
  {
    type: ASSET_CONTENT_REFERENCE_TYPE_1X1_WITH_TEXT,
    method: 'gen-fill',
    width: 3000,
    height: 3000,
    genFill: true,
  },
])

const TWO_BY_THREE_TARGETS = Object.freeze([
  { type: ASSET_CONTENT_REFERENCE_TYPE_KEYART_2X3_WITH_TEXT, method: 'copy', genFill: false },
  {
    type: ASSET_CONTENT_REFERENCE_TYPE_POSTER_3X4,
    method: 'gen-fill',
    width: 456,
    height: 608,
    genFill: true,
  },
  {
    type: ASSET_CONTENT_REFERENCE_TYPE_POSTER_IMAGE,
    method: 'gen-fill',
    width: 456,
    height: 608,
    genFill: true,
  },
])

const FAMILY_TARGETS = {
  'no-text-16x9': NO_TEXT_16X9_TARGETS,
  'with-text-16x9': WITH_TEXT_16X9_TARGETS,
  '2x3': TWO_BY_THREE_TARGETS,
}

export function getMasterFamily (slotType) {
  if (NO_TEXT_16X9_MASTERS.includes(slotType)) {
    return 'no-text-16x9'
  }
  if (WITH_TEXT_16X9_MASTERS.includes(slotType)) {
    return 'with-text-16x9'
  }
  if (TWO_BY_THREE_MASTERS.includes(slotType)) {
    return '2x3'
  }
  return null
}

export function isMasterSlotType (slotType) {
  return getMasterFamily(slotType) != null
}

export function getPrimarySlotType (family) {
  if (family === 'no-text-16x9') {
    return ASSET_CONTENT_REFERENCE_TYPE_D6_KEYART_NO_TEXT
  }
  if (family === 'with-text-16x9') {
    return ASSET_CONTENT_REFERENCE_TYPE_D6_KEYART_WITH_TEXT
  }
  return ASSET_CONTENT_REFERENCE_TYPE_KEYART_2X3_WITH_TEXT
}

export function getExpandTargets (slotType, options = {}) {
  const { skipTypes = [], contentType, includePrimary = true, squareSource } = options
  if (!contentType || contentType === CONTENT_TYPE_CLIP) {
    return []
  }
  const family = getMasterFamily(slotType)
  if (!family) {
    return []
  }
  if (contentType === CONTENT_TYPE_COURSE && family === '2x3') {
    return []
  }
  const skip = new Set(skipTypes)
  const primaryType = getPrimarySlotType(family)
  const targets = applySquareSource(family, FAMILY_TARGETS[family], squareSource)
  return targets.filter((target) => {
    if (!includePrimary && target.type === primaryType) {
      return false
    }
    if (
      contentType === CONTENT_TYPE_COURSE &&
      target.type === ASSET_CONTENT_REFERENCE_TYPE_KEYART_2X3_WITH_TEXT
    ) {
      return false
    }
    return !skip.has(target.type)
  })
}

export function getDerivedExpandTargets (slotType, options = {}) {
  return getExpandTargets(slotType, { ...options, includePrimary: false }).filter(
    (target) => target.type !== slotType,
  )
}

export const SQUARE_SOURCE_NONE = 'none'
export const SQUARE_SOURCE_16X9 = '16x9'
export const SQUARE_SOURCE_2X3 = '2x3'

const SQUARE_SOURCES = Object.freeze([SQUARE_SOURCE_NONE, SQUARE_SOURCE_16X9, SQUARE_SOURCE_2X3])

const ONE_BY_ONE_TYPES = Object.freeze([
  ASSET_CONTENT_REFERENCE_TYPE_1X1_NO_TEXT,
  ASSET_CONTENT_REFERENCE_TYPE_1X1_WITH_TEXT,
])

const ONE_BY_ONE_WITH_TEXT_TARGET = Object.freeze({
  type: ASSET_CONTENT_REFERENCE_TYPE_1X1_WITH_TEXT,
  method: 'gen-fill',
  width: 3000,
  height: 3000,
  genFill: true,
})

/**
 * Omitted means the historical 16x9 expansion so existing callers stay put.
 * The bulk-upload session defaults to none at the call site.
 */
export function normalizeSquareSource (source, options = {}) {
  const { omittedMeans16x9 = true } = options
  if (SQUARE_SOURCES.includes(source)) {
    return source
  }
  return omittedMeans16x9 ? SQUARE_SOURCE_16X9 : SQUARE_SOURCE_NONE
}

function isOneByOneType (type) {
  return ONE_BY_ONE_TYPES.includes(type)
}

/**
 * none drops every 1x1. 2x3 moves the with-text 1x1 onto the 2x3 master and
 * leaves the no-text 1x1 on the clean 16x9 master.
 */
export function applySquareSource (family, targets, squareSource) {
  const mode = normalizeSquareSource(squareSource)
  if (mode === SQUARE_SOURCE_16X9) {
    return targets
  }
  if (mode === SQUARE_SOURCE_NONE) {
    return targets.filter((target) => !isOneByOneType(target.type))
  }
  if (family === 'with-text-16x9') {
    return targets.filter((target) => target.type !== ASSET_CONTENT_REFERENCE_TYPE_1X1_WITH_TEXT)
  }
  if (family === '2x3') {
    return [...targets, ONE_BY_ONE_WITH_TEXT_TARGET]
  }
  return targets
}

/**
 * 1x1 types a 16x9 master must not persist for this square-source choice.
 */
export function squareSkipTypesForMaster (slotType, squareSource) {
  const mode = normalizeSquareSource(squareSource)
  const family = getMasterFamily(slotType)
  if (mode === SQUARE_SOURCE_16X9 || !family || family === '2x3') {
    return []
  }
  if (family === 'no-text-16x9') {
    return mode === SQUARE_SOURCE_NONE ? [ASSET_CONTENT_REFERENCE_TYPE_1X1_NO_TEXT] : []
  }
  return [ASSET_CONTENT_REFERENCE_TYPE_1X1_WITH_TEXT]
}

export const SLOT_FILL_EMPTY = 'empty'
export const SLOT_FILL_REPLACE = 'replace'
export const SLOT_FILL_NONE = 'none'

const SLOT_FILL_MODES = Object.freeze([SLOT_FILL_EMPTY, SLOT_FILL_REPLACE, SLOT_FILL_NONE])

/**
 * Unknown values stay on the default: fill slots that are empty.
 */
export function normalizeSlotFillMode (mode) {
  if (SLOT_FILL_MODES.includes(mode)) {
    return mode
  }
  return SLOT_FILL_EMPTY
}

/**
 * Slot types place-from-source must not write.
 * `none` skips every derived size and publishes only the uploaded file.
 * `empty` also skips sizes that already have a live image.
 * `replace` writes derived sizes even when a live image is there.
 */
export function buildSkipTypes ({
  liveTypes = [],
  explicitTypes = [],
  derivedTypes = [],
  slotFillMode = SLOT_FILL_EMPTY,
} = {}) {
  const mode = normalizeSlotFillMode(slotFillMode)
  if (mode === SLOT_FILL_NONE) {
    return [...new Set([...explicitTypes, ...derivedTypes])]
  }
  const skip = new Set(explicitTypes)
  if (mode !== SLOT_FILL_REPLACE) {
    liveTypes.forEach((type) => skip.add(type))
  }
  return [...skip]
}

export function previewNeedsUpscale (target = {}, sourceWidth, sourceHeight) {
  const width = Number(target.width)
  const height = Number(target.height)
  const sourceW = Number(sourceWidth)
  const sourceH = Number(sourceHeight)
  if (!Number.isFinite(sourceW) || !Number.isFinite(sourceH) || sourceW <= 0 || sourceH <= 0) {
    return undefined
  }
  return (
    (Number.isFinite(width) && width > sourceW) || (Number.isFinite(height) && height > sourceH)
  )
}

export function createAssetsRenderPreviewUrl (server, uuid, target = {}, seed = 1, sourceSize = {}) {
  if (!uuid) {
    return undefined
  }
  const params = new URLSearchParams()
  if (target.width) {
    params.set('w', String(target.width))
  }
  if (target.height) {
    params.set('h', String(target.height))
  }
  if (target.genFill) {
    params.set('genFill', 'true')
    params.set('fill-gen-seed', String(seed))
  }
  const upscale = previewNeedsUpscale(target, sourceSize.sourceWidth, sourceSize.sourceHeight)
  if (upscale !== undefined) {
    params.set('upscale', String(upscale))
  }
  const query = params.toString()
  const base = `${server}/v1/assets-render/${uuid}`
  return query ? `${base}?${query}` : base
}
